import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { PhoneBookRequest } from '../../types';
import { Loading } from '../../components/ui/Loading';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { StatusBadge } from '../../components/StatusBadge';

export const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    userApprovals: 0,
    phoneBookCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      const userId = user?.id;
      if (!userId) return;
      setLoading(true);
      try {
        const [requestsRes, approvalsRes, phoneBookRes] = await Promise.all([
          supabase.from('phone_book_requests').select('status').eq('requested_by', userId),
          supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .eq('role', 'USER')
            .eq('status', 'UNDER_REVIEW'),
          supabase
            .from('phone_book')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'APPROVED'),
        ]);
        if (requestsRes.error) throw requestsRes.error;
        if (approvalsRes.error) throw approvalsRes.error;
        if (phoneBookRes.error) throw phoneBookRes.error;
        const list = (requestsRes.data ?? []) as PhoneBookRequest[];
        setStats({
          pending: list.filter((r) => r.status === 'UNDER_REVIEW').length,
          approved: list.filter((r) => r.status === 'APPROVED').length,
          rejected: list.filter((r) => r.status === 'BLOCKED').length,
          userApprovals: approvalsRes.count ?? 0,
          phoneBookCount: phoneBookRes.count ?? 0,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user?.id]);

  if (loading) return <Loading full />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Employee Dashboard</h1>
      {error && <ErrorAlert message={error} />}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Pending Requests</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{stats.pending}</p>
          <StatusBadge status="UNDER_REVIEW" />
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Approved Requests</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{stats.approved}</p>
          <StatusBadge status="APPROVED" />
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Rejected Requests</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{stats.rejected}</p>
          <StatusBadge status="BLOCKED" />
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">User Approvals</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{stats.userApprovals}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-600">Phone Book Contacts</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{stats.phoneBookCount}</p>
        </div>
      </div>
    </div>
  );
};
